import React, { useState } from "react";
import Swal from "sweetalert2";

import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  Check,
  ChevronDown,
  Clock3,
  FileCheck2,
  FileText,
  GraduationCap,
  Headphones,
  Menu,
  MessageCircle,
  Phone,
  ShieldCheck,
  Truck,
  UserRound,
  Users,
  X,
  Globe2,
  Search,
  Send,
  LockKeyhole,
} from "lucide-react";

import "./HrdLp.css";
import logo from "./../../../vid_img/logo.png";

/* =========================================================
   FAQ DATA
========================================================= */

const faqs = [
  [
    "What is HRD Attestation?",
    "HRD attestation refers to the state-level authentication or verification route used for eligible educational documents before subsequent attestation or legalization steps, depending on the destination and document.",
  ],
  [
    "Which documents can be submitted for HRD attestation?",
    "Common educational documents may include degree certificates, diplomas, mark sheets and transcripts. Requirements depend on the issuing university/board, state authority and intended use.",
  ],
  [
    "How long does HRD attestation take?",
    "Processing time can vary according to the issuing institution, verification requirements and state-level process. We confirm the applicable route before proceeding.",
  ],
  [
    "Do I need to visit your office?",
    "Pickup and document assistance can be arranged across Bangalore, subject to the document and service requirements.",
  ],
  [
    "What is the cost of HRD attestation?",
    "The cost depends on the educational document, verification requirements, issuing authority and service handling. Request a document-specific quote.",
  ],
  [
    "Why is HRD attestation required?",
    "Some foreign-use processes require educational documents to be authenticated or verified at the appropriate state level before MEA or destination-country legalization. The exact route depends on the destination and document.",
  ],
  [
    "Can you pick up educational documents from Bangalore?",
    "Yes, pickup and delivery support can be arranged across Bangalore, with availability confirmed based on the document and service route.",
  ],
  [
    "Do you also provide MEA or embassy attestation?",
    "Additional MEA or destination-country attestation may be required after the relevant educational-document verification. The applicable route should be checked for the destination country.",
  ],
];

/* =========================================================
   DOCUMENT TYPES
========================================================= */

const docs = [
  ["Degree Certificate", GraduationCap],
  ["Diploma Certificate", FileText],
  ["Mark Sheet", FileCheck2],
  ["Academic Transcript", FileText],
  ["Provisional Certificate", ShieldCheck],
  ["Other Degree & Diploma Documents", Building2],
];

/* =========================================================
   COUNTRIES
========================================================= */

const countries = [
  ["🇦🇪", "UAE"],
  ["🇸🇦", "Saudi Arabia"],
  ["🇶🇦", "Qatar"],
  ["🇰🇼", "Kuwait"],
  ["🇴🇲", "Oman"],
  ["🇧🇭", "Bahrain"],
  ["🇬🇧", "UK"],
  ["🇨🇦", "Canada"],
  ["🇦🇺", "Australia"],
  ["🇩🇪", "Germany"],
];

/* =========================================================
   SERVICE CARDS
========================================================= */

const serviceCards = [
  [
    "Degree & Diploma Documents",
    GraduationCap,
    [
      "Degree Certificates",
      "Diplomas",
      "Engineering Degrees",
      "MBA / PG Degrees",
      "Professional Certificates",
    ],
  ],
  [
    "Academic Records",
    FileText,
    [
      "Mark Sheets",
      "Transcripts",
      "Provisional Certificates",
      "Consolidated Marksheets",
      "University Records",
    ],
  ],
  [
    "Verification Support",
    BadgeCheck,
    [
      "University Verification",
      "Registrar / Issuing Authority",
      "State Authentication Guidance",
      "MEA Route Guidance",
      "Destination Requirements",
    ],
  ],
];

/* =========================================================
   WHY GOODWAY
========================================================= */

const why = [
  [
    "Experienced Team",
    Users,
    "Experience with educational-document processes",
  ],
  [
    "Document Assistance",
    FileCheck2,
    "Help with requirements and verification",
  ],
  [
    "Bangalore Pickup",
    Truck,
    "Pickup options across Bangalore",
  ],
  [
    "Transparent Process",
    Clock3,
    "Clear communication and status updates",
  ],
  [
    "Destination Guidance",
    Globe2,
    "Support for international document use",
  ],
  [
    "Customer Support",
    Headphones,
    "We're here when you need us",
  ],
];

/* =========================================================
   PROCESS STEPS
========================================================= */

const steps = [
  [
    "01",
    "Share Your Details",
    FileText,
    "Tell us your qualification, document type and destination.",
  ],
  [
    "02",
    "Document Verification",
    Search,
    "We check the document and explain the applicable verification route.",
  ],
  [
    "03",
    "HRD Processing",
    BadgeCheck,
    "Your educational document is handled through the relevant verification process.",
  ],
  [
    "04",
    "Receive Your Documents",
    Send,
    "Get your processed documents via pickup or delivery.",
  ],
];

/* =========================================================
   MAIN COMPONENT
========================================================= */

const HrdLp = () => {
  const [open, setOpen] = useState(null);
  const [menu, setMenu] = useState(false);

  return (
    <div className="hrdLp-site">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="hrdLp-header">
        <div className="hrdLp-container hrdLp-nav">
          <a className="hrdLp-logo" href="#top">
            <b>
              <img src={logo} alt="Goodway Attestation" />
            </b>
          </a>

          <button
            className="hrdLp-menuBtn"
            onClick={() => setMenu(!menu)}
            type="button"
            aria-label="Toggle navigation"
          >
            {menu ? <X /> : <Menu />}
          </button>

          <nav className={menu ? "hrdLp-open" : ""}>
            <a href="#services">HRD Attestation</a>
            <a href="#documents">Document Types</a>
            <a href="#countries">Countries</a>
            <a href="#process">Process</a>
            <a href="#reviews">Reviews</a>
            <a href="#faq">FAQs</a>
          </nav>

          <div className="hrdLp-navActions">
            <a
              className="hrdLp-phone"
              href="tel:+919148889444"
            >
              <Phone size={16} />

              <span>
                +91 9148889444
                <small>Call Us Now</small>
              </span>
            </a>

            <a
              className="hrdLp-wa"
              href="https://wa.me/919148889444"
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle size={18} />

              WhatsApp

              <span>Chat with our Expert</span>
            </a>
          </div>
        </div>
      </header>

      {/* =====================================================
          MAIN
      ===================================================== */}

      <main id="top">
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="hrdLp-hero">
          <div className="hrdLp-heroGlow"></div>

          <div className="hrdLp-container hrdLp-heroGrid">
            <div className="hrdLp-heroCopy">
              <div className="hrdLp-eyebrow hrdLp-green">
                ● HRD ATTESTATION SERVICES IN BANGALORE
              </div>

              <h1>
                Get Your Degree & Diploma Documents{" "}
                <em>HRD Attested</em> in Bangalore
              </h1>

              <p>
                Professional HRD attestation support for degree
                certificates, diplomas, mark sheets and academic
                documents in Bangalore. Get document verification
                guidance and support for the next stage of your
                international documentation process.
              </p>

              <div className="hrdLp-featureRow">
                {[
                  [
                    Truck,
                    "Doorstep Pickup",
                    "Across Bangalore",
                  ],
                  [
                    FileCheck2,
                    "Expert Guidance",
                    "From document check to delivery",
                  ],
                  [
                    Clock3,
                    "Timely Processing",
                    "Transparent updates",
                  ],
                  [
                    ShieldCheck,
                    "Secure Handling",
                    "Your documents are safe with us",
                  ],
                ].map(([Icon, title, description]) => (
                  <div
                    className="hrdLp-feature"
                    key={title}
                  >
                    <span>
                      <Icon />
                    </span>

                    <b>{title}</b>

                    <small>{description}</small>
                  </div>
                ))}
              </div>

              {/* TRUST */}

              <div className="hrdLp-trust">
                <div className="hrdLp-google">
                  <strong>G</strong>

                  <div>
                    <b>
                      4.9/5 <span>★★★★★</span>
                    </b>

                    <small>
                      From 2,400+ happy customers
                    </small>
                  </div>
                </div>

                <div className="hrdLp-avatars">
                  <i>AK</i>
                  <i>PM</i>
                  <i>RS</i>
                  <i>SK</i>

                  <span>
                    Trusted by Students,
                    <br />
                    Professionals & Businesses
                  </span>
                </div>
              </div>

              {/* HERO BUTTONS */}

              <div className="hrdLp-heroBtns">
                <a
                  href="#quote"
                  className="hrdLp-btn hrdLp-primary"
                >
                  Get Free HRD Attestation Quote
                  <ArrowRight />
                </a>

                <a
                  href="https://wa.me/919148889444"
                  target="_blank"
                  rel="noreferrer"
                  className="hrdLp-btn hrdLp-outline"
                >
                  <MessageCircle />

                  WhatsApp an Expert

                  <small>Quick Response</small>
                </a>
              </div>
            </div>

            {/* HERO ART */}

            <HeroArt />

            {/* QUOTE FORM */}

            <QuoteForm />
          </div>
        </section>

        {/* =====================================================
            PROCESS
        ===================================================== */}

        <section
          id="process"
          className="hrdLp-section hrdLp-pale"
        >
          <SectionTitle
            tag="SIMPLE. TRANSPARENT. HASSLE-FREE"
            title={
              <>
                How <em>HRD Attestation</em> Works
              </>
            }
            sub="Get your educational documents checked and processed through the applicable HRD route."
          />

          <div className="hrdLp-container hrdLp-steps">
            {steps.map(
              ([number, title, Icon, description], index) => (
                <React.Fragment key={number}>
                  <div className="hrdLp-step">
                    <span className="hrdLp-num">
                      {number}
                    </span>

                    <div className="hrdLp-stepIcon">
                      <Icon />
                    </div>

                    <b>{title}</b>

                    <p>{description}</p>
                  </div>

                  {index < steps.length - 1 && (
                    <div className="hrdLp-arrow">
                      →
                    </div>
                  )}
                </React.Fragment>
              ),
            )}
          </div>

          <div className="hrdLp-center">
            <a
              className="hrdLp-btn hrdLp-primary"
              href="#quote"
            >
              Get My Free HRD Attestation Quote
              <ArrowRight />
            </a>
          </div>
        </section>

        {/* =====================================================
            DOCUMENTS
        ===================================================== */}

        <section
          id="documents"
          className="hrdLp-section"
        >
          <SectionTitle
            tag="POPULAR DOCUMENTS"
            title={
              <>
                Which Educational Document Needs{" "}
                <em>HRD Attestation?</em>
              </>
            }
            sub="Select your educational document type to get started."
          />

          <div className="hrdLp-container hrdLp-docGrid">
            {docs.map(([title, Icon]) => (
              <a
                href="#quote"
                className="hrdLp-docCard"
                key={title}
              >
                <span>
                  <Icon />
                </span>

                <b>{title}</b>

                <small>
                  Get Quote
                  <ArrowRight />
                </small>
              </a>
            ))}
          </div>

          <div className="hrdLp-center hrdLp-muted">
            Don't see your document?{" "}
            <a href="#quote">
              Talk to an Expert →
            </a>
          </div>
        </section>

        {/* =====================================================
            SERVICES
        ===================================================== */}

        <section
          id="services"
          className="hrdLp-section hrdLp-pale"
        >
          <div className="hrdLp-container hrdLp-serviceLayout">
            <div className="hrdLp-serviceIntro">
              <div className="hrdLp-eyebrow hrdLp-blue">
                ● OUR SERVICES
              </div>

              <h2>
                HRD Attestation Services
                <br />
                <em>We Provide</em>
              </h2>

              <p>
                End-to-end assistance for educational documents
                with document checks, verification guidance and
                processing support.
              </p>

              <a
                href="#quote"
                className="hrdLp-btn hrdLp-primary"
              >
                Get Free HRD Quote
                <ArrowRight />
              </a>
            </div>

            <div className="hrdLp-serviceCards">
              {serviceCards.map(
                ([title, Icon, list]) => (
                  <div
                    className="hrdLp-serviceCard"
                    key={title}
                  >
                    <Icon className="hrdLp-serviceIcon" />

                    <h3>{title}</h3>

                    {list.map((item) => (
                      <div
                        className="hrdLp-check"
                        key={item}
                      >
                        <Check />

                        {item}
                      </div>
                    ))}

                    <a href="#quote">
                      Get Quote →
                    </a>
                  </div>
                ),
              )}
            </div>
          </div>
        </section>

        {/* =====================================================
            WHY GOODWAY
        ===================================================== */}

        <section className="hrdLp-section">
          <SectionTitle
            tag="WHY GOODWAY"
            title={
              <>
                Why Choose <em>Goodway?</em>
              </>
            }
            sub="Reliable, Experienced, Customer-Focused."
          />

          <div className="hrdLp-container hrdLp-whyGrid">
            {why.map(
              ([title, Icon, description]) => (
                <div
                  className="hrdLp-whyCard"
                  key={title}
                >
                  <span>
                    <Icon />
                  </span>

                  <b>{title}</b>

                  <small>{description}</small>
                </div>
              ),
            )}
          </div>
        </section>

        {/* =====================================================
            COUNTRIES
        ===================================================== */}

        <section
          id="countries"
          className="hrdLp-section hrdLp-pale"
        >
          <SectionTitle
            tag="GLOBAL COVERAGE"
            title={
              <>
                HRD Attestation <em>Support</em> for Your
                Destination Needs
              </>
            }
            sub="We assist with educational-document verification and HRD attestation requirements for international use."
          />

          <div className="hrdLp-container hrdLp-countryGrid">
            {countries.map(([flag, country]) => (
              <div
                className="hrdLp-country"
                key={country}
              >
                <strong>{flag}</strong>

                <span>{country}</span>
              </div>
            ))}
          </div>

          <div className="hrdLp-center">
            <a
              href="#quote"
              className="hrdLp-btn hrdLp-outline hrdLp-small"
            >
              View All Countries
              <ArrowRight />
            </a>
          </div>
        </section>

        {/* =====================================================
            REVIEWS
        ===================================================== */}

        <section
          id="reviews"
          className="hrdLp-section hrdLp-reviews"
        >
          <SectionTitle
            tag="CUSTOMER REVIEWS"
            title={
              <>
                What Our Customers <em>Say</em>
              </>
            }
            sub="Real experiences from people who trusted Goodway."
          />

          <div className="hrdLp-container hrdLp-reviewGrid">
            {[
              [
                "“Very professional service. They explained the educational document process clearly and helped with the required verification.”",
                "Rohit S.",
                "Degree Certificate",
              ],
              [
                "“Smooth experience with clear updates. They helped us understand the documentation needed for our academic certificate.”",
                "Priya M.",
                "Diploma Certificate",
              ],
              [
                "“Excellent support for my degree document. The team guided me through the HRD-related requirements step by step.”",
                "Arun K.",
                "Degree Certificate",
              ],
            ].map(([quote, name, type]) => (
              <div
                className="hrdLp-review"
                key={name}
              >
                <div className="hrdLp-stars">
                  ★★★★★
                </div>

                <p>{quote}</p>

                <footer>
                  <i>{name.slice(0, 2)}</i>

                  <span>
                    <b>{name}</b>
                    <small>Bangalore</small>
                  </span>

                  <small>{type}</small>
                </footer>
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            FAQ
        ===================================================== */}

        <section
          id="faq"
          className="hrdLp-section hrdLp-faq"
        >
          <SectionTitle
            tag="FREQUENTLY ASKED QUESTIONS"
            title={
              <>
                Your Questions, <em>Answered</em>
              </>
            }
            sub="Quick answers about HRD attestation and educational-document verification in Bangalore."
          />

          <div className="hrdLp-container hrdLp-faqGrid">
            {faqs.map(
              ([question, answer], index) => (
                <div
                  className={
                    "hrdLp-faqItem " +
                    (open === index
                      ? "hrdLp-active"
                      : "")
                  }
                  key={question}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpen(
                        open === index
                          ? null
                          : index,
                      )
                    }
                  >
                    <span>{question}</span>

                    <ChevronDown />
                  </button>

                  {open === index && (
                    <p>{answer}</p>
                  )}
                </div>
              ),
            )}
          </div>
        </section>

        {/* =====================================================
            CTA
        ===================================================== */}

        <section
          id="quote"
          className="hrdLp-cta"
        >
          <div className="hrdLp-container hrdLp-ctaInner">
            <div>
              <h2>
                Need HRD Attestation in Bangalore?
              </h2>

              <p>
                Tell us your educational document and
                destination country. Our team will guide you
                on the applicable next steps.
              </p>
            </div>

            <a
              className="hrdLp-btn hrdLp-primary"
              href="#top"
            >
              Get Free HRD Attestation Quote
              <ArrowRight />
            </a>

            <a
              className="hrdLp-btn hrdLp-greenBtn"
              href="https://wa.me/919148889444"
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle />

              WhatsApp an Expert
            </a>

            <div className="hrdLp-call">
              <small>Prefer to speak?</small>

              <b>+91 9148889444</b>
            </div>
          </div>
        </section>

        {/* =====================================================
            OFFICE
        ===================================================== */}

        <section className="hrdLp-office">
          <div className="hrdLp-container hrdLp-officeGrid">
            <div className="hrdLp-map">
              <iframe
                title="Goodway Attestation Bangalore Location"
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7775.058287553612!2d77.572151!3d13.00194!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17d588d9caef%3A0x5ba382acaf05e1bd!2sGoodway%20Attestation%20%26%20Apostille%20Services%20in%20Bangalore%20Pvt.%20Ltd!5e0!3m2!1sen!2sus!4v1719668500894!5m2!1sen!2sus"
                height="200"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="map"
              ></iframe>
            </div>

            <div>
              <div className="hrdLp-eyebrow hrdLp-orange">
                ● BANGALORE OFFICE
              </div>

              <h2>Our Office in Bangalore</h2>

              <p>
                <b>Goodway Attestation</b>
                <br />
                #123, 4th Floor, XYZ Business Center,
                <br />
                Koramangala, Bangalore - 560034,
                Karnataka
              </p>

              <a
                className="hrdLp-btn hrdLp-outline hrdLp-small"
                href="#top"
              >
                Get Directions
                <ArrowRight />
              </a>
            </div>

            <div className="hrdLp-officeMeta">
              <div>
                <Clock3 />

                <b>Working Hours</b>

                <span>
                  Mon - Sat: 9:30 AM - 6:30 PM
                  <br />
                  Sunday: Closed
                </span>
              </div>

              <div>
                <Phone />

                <b>Call Us</b>

                <span>+91 9148889444</span>
              </div>

              <div>
                <MessageCircle />

                <b>WhatsApp</b>

                <span>Chat with our team</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <footer className="hrdLp-footer">
        <div className="hrdLp-container hrdLp-footGrid">
          <div>
            <a
              className="hrdLp-logo hrdLp-light"
              href="#top"
            >
              <img
                src={logo}
                alt="Goodway Attestation"
              />
            </a>

            <p>
              Your trusted partner for HRD attestation and
              educational document services in Bangalore.
            </p>

            <div className="hrdLp-social">
              f&nbsp;&nbsp;in&nbsp;&nbsp;◎&nbsp;&nbsp;▶
            </div>
          </div>

          <div>
            <h4>Quick Links</h4>

            <a href="#services">
              HRD Attestation
            </a>

            <a href="#documents">
              Document Types
            </a>

            <a href="#countries">
              Countries
            </a>

            <a href="#process">
              Process
            </a>

            <a href="#faq">
              FAQs
            </a>
          </div>

          <div>
            <h4>Our Services</h4>

            <a href="#services">
              Degree & Diploma Documents
            </a>

            <a href="#services">
              Academic Records
            </a>

            <a href="#services">
              Verification Support
            </a>

            <a href="#services">
              HRD Attestation
            </a>
          </div>

          <div>
            <h4>Contact Us</h4>

            <a href="tel:+919148889444">
              ☎ +91 9148889444
            </a>

            <a href="mailto:info@goodwayattestation.com">
              ✉ info@goodwayattestation.com
            </a>

            <a href="#top">
              ⌖ Koramangala, Bangalore
            </a>
          </div>

          <div>
            <h4>Legal</h4>

            <a href="#top">
              Privacy Policy
            </a>

            <a href="#top">
              Terms & Conditions
            </a>

            <a href="#top">
              Disclaimer
            </a>
          </div>
        </div>

        <div className="hrdLp-container hrdLp-copyright">
          <span>
            © 2026 Goodway Attestation. All rights reserved.
          </span>

          <span>
            Helping Your Documents Travel The World ✈
          </span>
        </div>
      </footer>
    </div>
  );
};

/* =========================================================
   SECTION TITLE COMPONENT
========================================================= */

function SectionTitle({ tag, title, sub }) {
  return (
    <div className="hrdLp-sectionTitle">
      <div className="hrdLp-eyebrow hrdLp-blue">
        ● {tag}
      </div>

      <h2>{title}</h2>

      <p>{sub}</p>
    </div>
  );
}

/* =========================================================
   HERO ART COMPONENT
========================================================= */

function HeroArt() {
  return (
    <div className="hrdLp-heroArt">
      <div className="hrdLp-skyline">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div className="hrdLp-landmark">
        🎓
      </div>

      <div className="hrdLp-passport">
        DEGREE

        <div className="hrdLp-globe">
          ◎
        </div>
      </div>

      <div className="hrdLp-apostilleDoc">
        <div className="hrdLp-docTop">
          HRD ATTESTATION
        </div>

        <small>
          EDUCATIONAL DOCUMENT
        </small>

        <div className="hrdLp-lines"></div>

        <div className="hrdLp-seal">
          HRD
          <br />
          VERIFIED
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   QUOTE FORM COMPONENT
========================================================= */

function QuoteForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    const form = event.currentTarget;

    const formData = new FormData(form);

    /* -------------------------------------------------------
       WEB3FORMS CONFIGURATION
    ------------------------------------------------------- */

    formData.append(
      "access_key",
      "f333e4b3-a1ed-4165-a06a-0db699456e2d",
    );

    formData.append(
      "subject",
      "New HRD Attestation Quote Request",
    );

    formData.append(
      "service",
      "HRD Attestation",
    );

    const object = Object.fromEntries(
      formData.entries(),
    );

    try {
      setIsSubmitting(true);

      const response = await fetch(
        "https://api.web3forms.com/submit",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          body: JSON.stringify(object),
        },
      );

      const result = await response.json();

      /* -----------------------------------------------------
         SUCCESS
      ----------------------------------------------------- */

      if (result.success) {
        await Swal.fire({
          title: "Request Sent!",
          text: "Your HRD attestation quote request has been submitted successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });

        form.reset();
      }

      /* -----------------------------------------------------
         WEB3FORMS ERROR
      ----------------------------------------------------- */

      else {
        await Swal.fire({
          title: "Submission Failed",
          text:
            result.message ||
            "Failed to send your request. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }

    /* -------------------------------------------------------
       NETWORK / JAVASCRIPT ERROR
    ------------------------------------------------------- */

    catch (error) {
      console.error(
        "HRD form submission error:",
        error,
      );

      await Swal.fire({
        title: "Something Went Wrong",
        text: "Unable to submit your request. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }

    /* -------------------------------------------------------
       FINISHED
    ------------------------------------------------------- */

    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="hrdLp-quoteForm"
      onSubmit={handleSubmit}
    >
      <h3>
        Get a Free HRD Attestation Quote
      </h3>

      <p>
        Tell us about your educational document,
        university and destination country. Our team
        will guide you through the applicable process,
        requirements and service cost.
      </p>

      {/* =====================================================
          FULL NAME
      ===================================================== */}

      <label>
        Full Name <i>*</i>

        <input
          required
          type="text"
          name="name"
          placeholder="Enter your full name"
          autoComplete="name"
        />
      </label>

      {/* =====================================================
          PHONE + CITY
      ===================================================== */}

      <div className="hrdLp-two">
        <label>
          Mobile Number <i>*</i>

          <input
            required
            type="tel"
            name="phone"
            placeholder="+91 10-digit number"
            autoComplete="tel"
            inputMode="tel"
          />
        </label>

        <label>
          City <i>*</i>

          <input
            required
            type="text"
            name="city"
            defaultValue="Bangalore"
            autoComplete="address-level2"
          />
        </label>
      </div>

      {/* =====================================================
          EMAIL
      ===================================================== */}

      <label>
        Email Address

        <input
          type="email"
          name="email"
          placeholder="you@example.com"
          autoComplete="email"
        />
      </label>

      {/* =====================================================
          DOCUMENT TYPE
      ===================================================== */}

      <label>
        Document Type <i>*</i>

        <select
          required
          name="document_type"
          defaultValue=""
        >
          <option
            value=""
            disabled
          >
            Select educational document
          </option>

          <option value="Degree Certificate">
            Degree Certificate
          </option>

          <option value="Diploma Certificate">
            Diploma Certificate
          </option>

          <option value="Mark Sheet">
            Mark Sheet
          </option>

          <option value="Transcript">
            Transcript
          </option>

          <option value="Provisional Certificate">
            Provisional Certificate
          </option>

          <option value="Other Educational Document">
            Other Educational Document
          </option>
        </select>
      </label>

      {/* =====================================================
          DESTINATION COUNTRY
      ===================================================== */}

      <label>
        Destination Country <i>*</i>

        <select
          required
          name="destination_country"
          defaultValue=""
        >
          <option
            value=""
            disabled
          >
            Select country
          </option>

          {countries.map(([, country]) => (
            <option
              key={country}
              value={country}
            >
              {country}
            </option>
          ))}
        </select>
      </label>

      {/* =====================================================
          SUBMIT BUTTON
      ===================================================== */}

      <button
        className="hrdLp-btn hrdLp-primary hrdLp-full"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          "Sending..."
        ) : (
          <>
            Get Free Quote Now
            <ArrowRight />
          </>
        )}
      </button>

      {/* =====================================================
          SECURITY MESSAGE
      ===================================================== */}

      <small className="hrdLp-secure">
        <LockKeyhole size={12} />

        Your information is secure. We never share your
        details.
      </small>
    </form>
  );
}

export default HrdLp;
